/* eslint-disable react-hooks/exhaustive-deps */
import { AppRootProvider } from '@jusbrasil-web/shared-app-root-provider'
import { useFrameBusHost } from '@jusbrasil-web/shared-frame-bus'
import { GTMProvider } from '@jusbrasil-web/shared-gtm'
import { MystiqueStylesheet } from '@jusbrasil-web/shared-mystique'
import Head from 'next/head'
import objectHash from 'object-hash'
import { useEffect, useRef } from 'react'

import { BoxLockModalEventData } from '../../types'

let alreadyImported = false

export type BoxPageBaseProps = {
  id: string
  enableResizeListener?: boolean
  enableCache?: boolean
}

export const BoxPageBase = (props: BoxPageBaseProps) => {
  const { id, enableResizeListener = false, enableCache = false } = props

  const currentKey = useRef<string | null>(null)
  const cacheRef = useRef<{ [key: string]: boolean }>({})

  const frame = useFrameBusHost<{
    onInitialize(): void
    setReady(ready: boolean): void
    resize: (sizes: { height: number } | { error: 'NOT_SUPPORTED' }) => void
  }>({
    id,
    autoConnect: false,
    listeners: {
      initBoxLockModal(eventData: BoxLockModalEventData) {
        window.initBoxLockModal?.(eventData)
        if (enableCache) {
          const key = objectHash(eventData)
          currentKey.current = key
        }
      },
      showBoxLockModal(eventData: BoxLockModalEventData) {
        window.showBoxLockModal?.(eventData)
      },
      syncWithCache(eventData: BoxLockModalEventData) {
        if (!enableCache) return
        const key = objectHash(eventData)
        const ready = cacheRef.current[key]
        if (ready) frame.trigger('setReady', true)
      },
    },
  })

  useEffect(() => {
    window.onLoadingChangeBoxLockModal = (loading) => {
      const ready = !loading
      frame.trigger('setReady', ready)
      if (enableCache && currentKey.current) {
        cacheRef.current[currentKey.current] = ready
      }
    }

    const prevOnCloseCallback = window.onCloseBoxLockModal
    window.onCloseBoxLockModal = () => {
      prevOnCloseCallback?.()
      frame.close()
    }

    if (!alreadyImported) {
      alreadyImported = true
      // See https://github.com/jusbrasil/box-js/tree/master/packages/box-unique-feature-modal-standalone
      // @ts-expect-error this module does not export anything, just alters window object
      import('@jusbrasil/box-unique-feature-modal-standalone')
      document.body.style.background = 'transparent'
      document.body.style.overflowY = 'auto'
    }
  }, [])

  // this code is responsible for updating the height of the iframe when the content changes
  useEffect(() => {
    if (!enableResizeListener) return
    if (!window.ResizeObserver) {
      frame.trigger('resize', { error: 'NOT_SUPPORTED' })
      return
    }

    let currHeight = 0
    const handler: ResizeObserverCallback = (entries) => {
      const contentHeight = entries[0].contentRect.height
      if (contentHeight && contentHeight !== currHeight) {
        currHeight = contentHeight
        frame.trigger('resize', { height: contentHeight })
      }
    }
    const observer = new ResizeObserver(handler)
    observer.observe(document.body)
    return () => observer.disconnect()
  }, [enableResizeListener])

  useEffect(() => waitForConnect(() => frame.connect()), [])

  useEffect(() => frame.trigger('onInitialize'), [])

  return (
    <AppRootProvider pageProps={props}>
      <style jsx global>{`
        body {
          min-height: unset;
        }
      `}</style>
      <Head>
        <title>Jusbrasil</title>
      </Head>
      <MystiqueStylesheet />
      <GTMProvider appId="web-shared-feature-locker" />
    </AppRootProvider>
  )
}

function waitForConnect(cb: () => void, attempts = 0) {
  if (detectBoxUniqueFeatureModalReactComponentIsMounted()) {
    cb()
  } else {
    setTimeout(() => waitForConnect(cb, attempts + 1), 100)
  }
}

function detectBoxUniqueFeatureModalReactComponentIsMounted() {
  if (!window.initBoxLockModal) return false
  // standalone - has queue property
  // react component - does not have queue property
  return !('queue' in window.initBoxLockModal)
}
